import {useRef, useCallback} from 'react';

type Corner = 'nw' | 'ne' | 'sw' | 'se';

interface UseCropInteractionsProps {
    moveCrop: (dx: number, dy: number) => void;
    resizeCrop: (corner: Corner, dx: number, dy: number) => void;
}

export const useCropInteractions = ({moveCrop, resizeCrop}: UseCropInteractionsProps) => {
    const dragStart = useRef({x: 0, y: 0});
    const isDragging = useRef(false);
    const isResizing = useRef(false);
    const resizeCorner = useRef<Corner | ''>('');

    const onMouseMove = useCallback((e: React.MouseEvent) => {
        if (!isDragging.current && !isResizing.current) return;
        e.preventDefault();
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        dragStart.current = {x: e.clientX, y: e.clientY};

        if (isDragging.current) moveCrop(dx, dy);
        else if (isResizing.current && resizeCorner.current) resizeCrop(resizeCorner.current as Corner, dx, dy);
    }, [moveCrop, resizeCrop]);

    const onCropMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        isDragging.current = true;
        dragStart.current = {x: e.clientX, y: e.clientY};
    }, []);

    const onResizeMouseDown = useCallback((corner: Corner) => (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        isResizing.current = true;
        resizeCorner.current = corner;
        dragStart.current = {x: e.clientX, y: e.clientY};
    }, []);

    const onMouseUp = useCallback(() => {
        isDragging.current = false;
        isResizing.current = false;
    }, []);

    return {onMouseMove, onCropMouseDown, onResizeMouseDown, onMouseUp};
};
